import { Maybe, MaybeUndef, Undef } from 'types/UtilityTypes';
import {
  GenericSuggestion,
  SuggestionPipelines,
} from 'types/suggestions/CoreSuggestions';
import {
  LoadBuildingSuggestions,
  ReferenceCandidate,
} from 'types/suggestions/LoadBuildingSuggestions';

import { ReferenceTypeOption } from './types';

// Reference numbers compare without case or surrounding spaces, so "po-12 " matches "PO-12".
export function normalizeReferenceNumber(value: string): string {
  return value.trim().toUpperCase();
}

// Reads the candidates of a clicked Builder suggestion. Drops blank numbers, numbers with a comma
// and repeats, keeping the first copy in the order Builder sent.
export function getReferenceCandidates(
  suggestion: Maybe<GenericSuggestion>
): ReferenceCandidate[] {
  if (suggestion?.pipeline !== SuggestionPipelines.LoadBuilding) {
    return [];
  }

  const raw = toCandidateList(
    (suggestion as LoadBuildingSuggestions).suggested?.referenceCandidates
  );

  const seen = new Set<string>();
  const candidates: ReferenceCandidate[] = [];
  raw.forEach((candidate) => {
    const number = String(candidate?.number ?? '').trim();
    const key = normalizeReferenceNumber(number);
    // Reference fields store comma-separated lists, so a number with a comma can't be one value.
    if (!number || number.includes(',') || seen.has(key)) {
      return;
    }
    seen.add(key);
    candidates.push({ ...candidate, number });
  });
  return candidates;
}

// Drops candidates already used in any reference field. Values may be comma-separated lists.
export function getUnusedReferenceCandidates(
  candidates: ReferenceCandidate[],
  usedValues: MaybeUndef<string>[]
): ReferenceCandidate[] {
  const used = new Set(
    usedValues
      .flatMap((value) => (value ?? '').split(','))
      .map(normalizeReferenceNumber)
      .filter(Boolean)
  );
  return candidates.filter(
    (candidate) => !used.has(normalizeReferenceNumber(candidate.number))
  );
}

// Matches the backend's suggested qualifier to one of the TMS's reference types by code, then
// by label. The backend sends TMS codes, so there is no alias mapping here.
export function findReferenceType(
  suggestedQualifier: Undef<string>,
  referenceTypes: ReferenceTypeOption[]
): Undef<ReferenceTypeOption> {
  const qualifier = suggestedQualifier?.trim().toLowerCase();
  if (!qualifier) {
    return undefined;
  }

  return (
    referenceTypes.find((type) => type.value.toLowerCase() === qualifier) ??
    referenceTypes.find((type) => type.label.trim().toLowerCase() === qualifier)
  );
}

// Appends a number to a comma-separated reference value unless it is already there.
export function appendReferenceValue(
  current: MaybeUndef<string>,
  number: string
): string {
  const values = (current ?? '')
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean);
  const key = normalizeReferenceNumber(number);
  if (values.some((value) => normalizeReferenceNumber(value) === key)) {
    return values.join(',');
  }
  return [...values, number.trim()].join(',');
}

// Suggestion merges in the sidebar can turn an array into an index-keyed object (the same thing
// LoadRefPillInput handles for refNumberCandidates), so both shapes are read in order.
function toCandidateList(raw: unknown): ReferenceCandidate[] {
  if (Array.isArray(raw)) {
    return raw;
  }
  if (raw && typeof raw === 'object') {
    return Object.values(raw as Record<string, ReferenceCandidate>);
  }
  return [];
}
